import Button from "components/button";
import Checkbox from "components/checkbox";
import DateRange from "components/date-range";
import GenericModal from "components/generic-modal"
import Input from "components/input";
import { useToastContext } from "hooks/useToastContext";
import { getLanguageCode } from "locales";
import moment from "moment";
import { useGridParameterizationContext } from "pages/master-data/unit-management/grid-parameterization/context";
import {
  gridParameterizationType,
  unloadCapacityRadioValues,
} from "pages/master-data/unit-management/grid-parameterization/utils";
import { type ChangeEvent, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { SaveLoadCapacitiesInMass } from "services/load-capacities.service";
import { SaveUnloadCapacitiesInMass } from "services/unload-capacities.service";
import type { IMassGridCapacitiesDto } from "types/capacity";
import { isEmpty } from "utils/custom-functions";
import { getWeekdaysFromAnInterval } from "utils/date";
import { ISO_DATE_FORMAT } from "utils/format-date";
import { formatErrorMessage } from "utils/handle-error";
import {
  CheckboxLabel,
  Line,
  ModalContent,
  ModalFooter,
  ModalTitle,
  WeekdayButton,
} from "./styled";

interface IModalEditMultipleDatesProps {
  type: string;
  open: boolean;
  handleClose: () => void;
  calendarEndDate: Date;
  handleGetCalendarDataAsync: () => Promise<void>;
}

type IDateRange = {
  From: string;
  To: string;
};

type DayOfWeek = {
  fullName: string;
  firstLetter: string;
  selected: boolean;
  dayValue: number;
  disabled: boolean;
};

const ModalEditMultipleDates = ({
  type,
  open,
  handleClose,
  calendarEndDate,
  handleGetCalendarDataAsync,
}: IModalEditMultipleDatesProps) => {
  const { openToast } = useToastContext();
  const {
    startLoading,
    stopLoading,
    filtersLoadCapacity,
    filtersUnloadCapacity,
    selectedRadioUnloadCapacity,
  } = useGridParameterizationContext();
  const [capacity, setCapacity] = useState<string>();
  const [dateRange, setDateRange] = useState<IDateRange>({
    From: "",
    To: "",
  });
  const [clearDates, setClearDates] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>(() => {
    const localeData = moment.localeData(getLanguageCode());
    const weekdaysMin = localeData.weekdays();
    return weekdaysMin.map((x, index) => ({
      fullName: x,
      firstLetter: x[0].toUpperCase(),
      selected: false,
      dayValue: index,
      disabled: false,
    }));
  });

  const handleChangeCapacity = (value: string) => {
    if (value === "" || (/^\d+$/.test(value) && Number(value) <= 1000)) {
      setCapacity(value);
    }
  };

  const handleChangeDateRange = (prop: string, value: string) => {
    setDateRange((prevState) => ({
      ...prevState,
      [prop]: value,
    }));
  };

  const handleClickDayOfWeek = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const _day = ev.currentTarget.value;

    setDaysOfWeek((prevState) =>
      prevState.map((x) => ({
        ...x,
        selected: x.fullName.toLowerCase() === _day.toLowerCase() ? !x.selected : x.selected,
      }))
    );
  };

  const handleCloseModal = () => {
    setCapacity(undefined);
    setClearDates(false);
    setDateRange({
      From: "",
      To: "",
    });
    setDaysOfWeek((prevState) =>
      prevState.map((x) => ({
        ...x,
        selected: false,
        disabled: false,
      }))
    );
    handleClose();
  };

  const handleSave = async () => {
    if (!clearDates && (!capacity || capacity?.length === 0)) {
      openToast(
        intl.get("master-data.managementUnity.grid.general-warning-messages.capacity-is-required"),
        "negative"
      );
      return;
    } else if (dateRange.From?.length === 0 || dateRange.To?.length === 0) {
      openToast(
        intl.get("master-data.managementUnity.grid.general-warning-messages.provide-a-date-range"),
        "negative"
      );
      return;
    } else if (!daysOfWeek.find((x) => x.selected)) {
      openToast(
        intl.get("master-data.managementUnity.grid.general-warning-messages.select-a-week-day"),
        "negative"
      );
      return;
    }

    await saveGridCapacities();
  };

  const saveGridCapacities = async () => {
    try {
      startLoading();
      const body: IMassGridCapacitiesDto = {
        BusinessUnitId:
          type === gridParameterizationType.LoadCapacity
            ? filtersLoadCapacity.businessUnitId
            : filtersUnloadCapacity.businessUnitId,
        MaterialGroupId:
          type === gridParameterizationType.UnloadCapacity &&
          selectedRadioUnloadCapacity === unloadCapacityRadioValues.materialGroup
            ? filtersUnloadCapacity.materialGroupId
            : undefined,
        FamilyId:
          type === gridParameterizationType.UnloadCapacity &&
          selectedRadioUnloadCapacity === unloadCapacityRadioValues.family
            ? filtersUnloadCapacity.familyId
            : undefined,
        SupplierId:
          type === gridParameterizationType.LoadCapacity
            ? filtersLoadCapacity.supplierId
            : undefined,
        Capacity: !clearDates ? Number(capacity) : null,
        FromDate: dateRange.From,
        ToDate: dateRange.To,
        ClearDates: clearDates,
        DaysOfWeek: daysOfWeek.filter((x) => x.selected)?.map((y) => y.dayValue),
      };

      if (type === gridParameterizationType.LoadCapacity) {
        await SaveLoadCapacitiesInMass(body);
      } else {
        await SaveUnloadCapacitiesInMass(body);
      }

      openToast(
        intl.get(
          "master-data.managementUnity.grid.general-warning-messages.all-changes-saved-successfully"
        ),
        "positive"
      );
    } catch (error) {
      openToast(formatErrorMessage(error), "negative");
    } finally {
      stopLoading();
      handleCloseModal();
      handleGetCalendarDataAsync();
    }
  };

  const isDayButtonDisabled = (day: DayOfWeek) =>
    day.disabled || isEmpty(dateRange.From) || isEmpty(dateRange.To);

  useEffect(() => {
    if (!isEmpty(dateRange.From) && isEmpty(dateRange.To)) {
      setDaysOfWeek((prevState) =>
        prevState.map((x) => ({
          ...x,
          selected: true,
          disabled: false,
        }))
      );
    } else if (isEmpty(dateRange.From)) {
      setDaysOfWeek((prevState) =>
        prevState.map((x) => ({
          ...x,
          selected: false,
          disabled: false,
        }))
      );
    } else if (!isEmpty(dateRange.From) && !isEmpty(dateRange.To)) {
      const weekdaysInterval = getWeekdaysFromAnInterval(dateRange.From, dateRange.To);
      setDaysOfWeek((prevState) =>
        prevState.map((x) => ({
          ...x,
          selected: weekdaysInterval.includes(x.fullName.toLowerCase()),
          disabled: !weekdaysInterval.includes(x.fullName.toLowerCase()),
        }))
      );
    }
  }, [dateRange]);

  return (
    <GenericModal open={open} onRequestClose={handleCloseModal} modalStyle={{ width: "568px" }}>
      <ModalTitle>
        {intl.get("master-data.managementUnity.grid.generalActions.editMultipleDates")}
      </ModalTitle>
      <ModalContent>
        <Line>
          <Input
            name="capacity"
            label={
              type === gridParameterizationType.UnloadCapacity
                ? intl.get("master-data.managementUnity.grid.dischargeDegree.header.title")
                : intl.get("master-data.managementUnity.grid.loadingGrid.header.title")
            }
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={capacity}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeCapacity(e.target.value)}
            disabled={clearDates}
            className="input-capacity"
          />
          <DateRange
            dataDe={dateRange.From}
            dataAte={dateRange.To}
            onChangeDataDe={(value: string) => handleChangeDateRange("From", value)}
            onChangeDataAte={(value: string) => handleChangeDateRange("To", value)}
            maxDate={moment(calendarEndDate).format(ISO_DATE_FORMAT)}
          />
        </Line>
        <Line className="checkbox-line">
          <Checkbox
            checked={clearDates}
            onChange={() => setClearDates((prevState) => !prevState)}
            className="checkbox-clear-dates"
          />
          <CheckboxLabel>
            {intl.get("master-data.managementUnity.grid.generalActions.clearDates")}
          </CheckboxLabel>
        </Line>
        <Line className="days-label">
          <CheckboxLabel>{intl.get("commons.days")}:</CheckboxLabel>
        </Line>
        <Line>
          {daysOfWeek.map((day) => (
            <WeekdayButton
              key={day.fullName}
              selected={day.selected}
              value={day.fullName}
              onClick={(ev) => handleClickDayOfWeek(ev)}
              title={day.fullName}
              disabled={isDayButtonDisabled(day)}
            >
              {day.firstLetter}
            </WeekdayButton>
          ))}
        </Line>
      </ModalContent>
      <ModalFooter>
        <Button value={intl.get("geral.buttonsDefault.cancel")} onClick={handleCloseModal} />
        <Button
          value={intl.get("geral.buttonsDefault.save")}
          buttonType="primary"
          onClick={handleSave}
        />
      </ModalFooter>
    </GenericModal>
  );
};

export default ModalEditMultipleDates;